import type { SVGProps } from 'react';

/**
 * Material Symbols Outlined glyphs (weight 300, optical size 24) used by the
 * DubaiExpress cabinet mockup, inlined from Google's material-design-icons.
 */
type IconProps = Pick<SVGProps<SVGSVGElement>, 'className'>;

/** Material Symbol `location_on`. */
export const LocationOnIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M480.07-485.39q29.85 0 51.04-21.26 21.2-21.26 21.2-51.11 0-29.85-21.26-51.05Q509.79-630 479.93-630q-29.85 0-51.04 21.26-21.2 21.26-21.2 51.12 0 29.85 21.26 51.04 21.26 21.19 51.12 21.19ZM480-179.46q117.38-105.08 179.65-201.58 62.27-96.5 62.27-169.04 0-109.38-69.5-179.84-69.5-70.46-172.42-70.46-102.92 0-172.42 70.46-69.5 70.46-69.5 179.84 0 72.54 62.27 169.04 62.27 96.5 179.65 201.58Zm0 79.84Q329-230.46 253.54-343.15q-75.46-112.7-75.46-206.93 0-138.46 89.57-224.19Q357.23-860 480-860t212.35 85.73q89.57 85.73 89.57 224.19 0 94.23-75.46 206.93Q631-230.46 480-99.62Zm0-458.07Z" />
  </svg>
);

/** Material Symbol `schedule`. */
export const ScheduleIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="m618.92-298.92 42.16-42.16L510-492.16V-680h-60v212.15l168.92 168.93ZM480.07-100q-78.84 0-148.21-29.92t-120.68-81.21q-51.31-51.29-81.25-120.63Q100-401.1 100-479.93q0-78.84 29.92-148.21t81.21-120.68q51.29-51.31 120.63-81.25Q401.1-860 479.93-860q78.84 0 148.21 29.92t120.68 81.21q51.31 51.29 81.25 120.63Q860-558.9 860-480.07q0 78.84-29.92 148.21t-81.21 120.68q-51.29 51.31-120.63 81.25Q558.9-100 480.07-100ZM480-480Zm0 320q133 0 226.5-93.5T800-480q0-133-93.5-226.5T480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160Z" />
  </svg>
);

/** Material Symbol `calendar_today`. */
export const CalendarTodayIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M212.31-100Q182-100 161-121q-21-21-21-51.31v-535.38Q140-738 161-759q21-21 51.31-21h55.38v-84.61h61.54V-780h303.08v-84.61h60V-780h55.38Q778-780 799-759q21 21 21 51.31v535.38Q820-142 799-121q-21 21-51.31 21H212.31Zm0-60h535.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-375.38H200v375.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85ZM200-607.69h560v-100q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H212.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v100Zm0 0V-720v112.31Z" />
  </svg>
);

/** Material Symbol `person`. */
export const PersonIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M480-492.31q-57.75 0-98.87-41.12Q340-574.56 340-632.31q0-57.75 41.13-98.87 41.12-41.13 98.87-41.13 57.75 0 98.87 41.13Q620-690.06 620-632.31q0 57.75-41.13 98.88-41.12 41.12-98.87 41.12ZM180-187.69v-88.93q0-29.38 15.96-54.42 15.96-25.04 42.66-38.5 59.3-29.07 119.65-43.61 60.35-14.54 121.73-14.54t121.73 14.54q60.35 14.54 119.65 43.61 26.7 13.46 42.66 38.5Q780-306 780-276.62v88.93H180Zm60-60h480v-28.93q0-12.15-7.04-22.5-7.04-10.34-19.11-16.88-51.7-25.46-105.42-38.58Q534.7-367.69 480-367.69q-54.7 0-108.43 13.11-53.72 13.12-105.42 38.58-12.07 6.54-19.11 16.88-7.04 10.35-7.04 22.5v28.93Zm240-304.62q33 0 56.5-23.5t23.5-56.5q0-33-23.5-56.5t-56.5-23.5q-33 0-56.5 23.5t-23.5 56.5q0 33 23.5 56.5t56.5 23.5Zm0-80Zm0 384.62Z" />
  </svg>
);

/** Material Symbol `local_shipping`. */
export const LocalShippingIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M233.76-177.69q-46.84 0-79.53-32.76T121.54-290H53.85v-417.69q0-30.31 21-51.31 21-21 51.3-21h544.62v152.31h106.15l129.23 173.08V-290h-72.3q0 46.79-32.79 79.55-32.78 32.76-79.61 32.76t-79.53-32.76q-32.69-32.76-32.69-79.55H346.15q0 46.92-32.78 79.61-32.78 32.7-79.61 32.7Zm.09-60q22 0 37.15-15.16Q286.15-268 286.15-290T271-327.16q-15.15-15.15-37.15-15.15t-37.16 15.15Q181.54-312 181.54-290q0 22 15.15 37.15 15.16 15.16 37.16 15.16ZM113.85-350h28.92q12.77-22.23 36.88-37.27 24.12-15.04 54.2-15.04 29.3 0 53.8 14.85 24.5 14.85 37.27 37.46h285.85v-370H126.15q-4.61 0-8.46 3.85-3.84 3.84-3.84 8.46V-350Zm607.69 112.31q22 0 37.15-15.16Q773.85-268 773.85-290t-15.16-37.16q-15.15-15.15-37.15-15.15t-37.16 15.15Q669.23-312 669.23-290q0 22 15.15 37.15 15.16 15.16 37.16 15.16ZM670.77-430H850L746.15-567.69h-75.38V-430ZM362.31-535Z" />
  </svg>
);

/** Material Symbol `content_copy`. */
export const ContentCopyIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M362.31-260Q332-260 311-281q-21-21-21-51.31v-455.38Q290-818 311-839q21-21 51.31-21h335.38Q728-860 749-839q21 21 21 51.31v455.38Q770-302 749-281q-21 21-51.31 21H362.31Zm0-60h335.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-455.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H362.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v455.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85Zm-140 200Q192-120 171-141q-21-21-21-51.31v-515.38h60v515.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h395.38v60H222.31ZM350-320v-480 480Z" />
  </svg>
);

/** Material Symbol `check`. */
export const CheckIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M382-253.85 168.62-467.23 211.38-510 382-339.38 748.62-706l42.76 42.77L382-253.85Z" />
  </svg>
);

/** Material Symbol `chevron_right`. */
export const ChevronRightIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="m517.85-480-184-184L376-706.15 602.15-480 376-253.85 333.85-296l184-184Z" />
  </svg>
);

/** Material Symbol `arrow_back`. */
export const ArrowBackIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="m294.92-450 227.85 227.85L480-180 180-480l300-300 42.77 42.15L294.92-510H780v60H294.92Z" />
  </svg>
);

/** Material Symbol `close`. */
export const CloseIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M256-213.85 213.85-256l224-224-224-224L256-746.15l224 224 224-224L746.15-704l-224 224 224 224L704-213.85l-224-224-224 224Z" />
  </svg>
);

/** Material Symbol `edit`. */
export const EditIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M200-200h50.46l409.46-409.46-50.46-50.46L200-250.46V-200Zm-60 60v-135.38l527.62-527.39q9.07-8.24 20.03-12.73 10.97-4.5 23-4.5t23.3 4.27q11.28 4.27 19.97 13.58l48.85 49.46q9.31 8.69 13.27 20 3.96 11.31 3.96 22.62 0 12.07-4.12 23.03-4.12 10.97-13.11 20.04L275.38-140H140Zm620.38-570.15-50.23-50.23 50.23 50.23Zm-126.13 75.9-24.79-25.67 50.46 50.46-25.67-24.79Z" />
  </svg>
);

/** Material Symbol `event`. */
export const EventIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M587.69-240q-38.54 0-65.42-26.88-26.88-26.89-26.88-65.43 0-38.54 26.88-65.42 26.88-26.88 65.42-26.88 38.54 0 65.43 26.88Q680-370.85 680-332.31q0 38.54-26.88 65.43Q626.23-240 587.69-240ZM212.31-100Q182-100 161-121q-21-21-21-51.31v-535.38Q140-738 161-759q21-21 51.31-21h55.38v-84.61h61.54V-780h303.08v-84.61h60V-780h55.38Q778-780 799-759q21 21 21 51.31v535.38Q820-142 799-121q-21 21-51.31 21H212.31Zm0-60h535.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-375.38H200v375.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85ZM200-607.69h560v-100q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H212.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v100Zm0 0V-720v112.31Z" />
  </svg>
);

/** Material Symbol `task_alt`. */
export const TaskAltIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M480.07-100q-78.84 0-148.21-29.92t-120.68-81.21q-51.31-51.29-81.25-120.63Q100-401.1 100-479.93q0-78.84 29.93-148.21 29.92-69.37 81.22-120.68t120.65-81.25Q401.15-860 480-860q63.2 0 119.6 19 56.4 19 103.48 53l-43.39 44.39q-38.77-26.7-83.99-41.54Q530.48-800 480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160q133 0 226.5-93.5T800-480q0-21.08-2.77-41.69t-8.31-40.08l48.46-48.84q11 30.84 16.81 63.46Q860-514.54 860-480q0 78.85-29.92 148.2t-81.21 120.65q-51.29 51.3-120.63 81.22Q558.9-100 480.07-100Zm-56.84-209.85L267.08-466l42.15-42.15 114 114 394.62-395.23L860-747.23 423.23-309.85Z" />
  </svg>
);

/** Material Symbol `inventory_2`. */
export const Inventory2Icon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M212.31-100q-29.92 0-51.12-21.19Q140-142.39 140-172.31v-447.92q-17.61-9.08-28.81-25.81Q100-662.77 100-684.62v-103.07q0-29.92 21.19-51.12Q142.39-860 172.31-860h615.38q29.92 0 51.12 21.19Q860-817.61 860-787.69v103.07q0 21.85-11.19 38.58-11.2 16.73-28.81 25.81v447.92q0 29.92-21.19 51.12Q777.61-100 747.69-100H212.31ZM200-612.31v438.08q0 6.15 4.42 10.19 4.43 4.04 10.97 4.04h532.3q5.39 0 8.85-3.46t3.46-8.85v-440H200Zm-27.69-60h615.38q5.39 0 8.85-3.46t3.46-8.85v-103.07q0-5.39-3.46-8.85t-8.85-3.46H172.31q-5.39 0-8.85 3.46t-3.46 8.85v103.07q0 5.39 3.46 8.85t8.85 3.46Zm195.38 249.62h224.62V-480H367.69v57.31ZM480-386.15Z" />
  </svg>
);

/** Material Symbol `package_2`. */
export const Package2Icon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M450-177.23v-285.54L200-607.54v278.62q0 3.07 1.54 5.77 1.54 2.69 4.61 4.61L450-177.23Zm60 0 243.85-141.31q3.07-1.92 4.61-4.61 1.54-2.7 1.54-5.77v-278.62L510-462.77v285.54Zm-66.15 65.46L176.16-265.85q-17.08-9.84-26.62-26.3-9.54-16.47-9.54-36.16v-303.38q0-19.69 9.54-36.16 9.54-16.46 26.62-26.3l267.69-154.08q17.07-9.85 36.15-9.85t36.15 9.85l267.69 154.08q17.08 9.84 26.62 26.3 9.54 16.47 9.54 36.16v303.38q0 19.69-9.54 36.16-9.54 16.46-26.62 26.3L516.15-111.77q-17.07 9.85-36.15 9.85t-36.15-9.85ZM634.23-604 727-657.23 486.15-796.54q-3.07-1.92-6.15-1.92-3.08 0-6.15 1.92l-86.85 50L634.23-604ZM480-514.46l93-53.85-247-142.77-93 53.85 247 142.77Z" />
  </svg>
);

/** Material Symbol `flight`. */
export const FlightIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M352.69-100v-46.92l77.7-57.7v-227.3L100-334.23v-61.54l330.39-195.38v-219.62q0-20.77 14.42-35.19 14.42-14.42 35.19-14.42 20.77 0 35.19 14.42 14.42 14.42 14.42 35.19v219.62L860-395.77v61.54l-330.39-97.69v226.53l77.7 58.47V-100L480-136.92 352.69-100Z" />
  </svg>
);

/** Material Symbol `delivery_truck_speed`. */
export const DeliveryTruckSpeedIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M290-180q-45.77 0-77.88-32.12Q180-244.23 180-290H85.39l13.38-60h101.85q13.92-22.85 37.5-36.42Q261.69-400 290-400q28.31 0 51.89 13.58 23.57 13.57 37.5 36.42h175.84l85.93-370h-438l2.46-10.46q4.46-21.85 21.34-35.69Q243.85-780 266.69-780h449.46l-34.69 150h100.08l114.23 152.31L858.46-290h-63.08q0 45.77-32.11 77.88Q731.15-180 685.39-180q-45.77 0-77.89-32.12-32.11-32.11-32.11-77.88H400q0 45.77-32.12 77.88Q335.77-180 290-180Zm346.23-255h189.54l5.54-28.31L751.54-570h-83.85l-31.46 135Zm-.92-261.46 5.85-23.54-85.93 370 5.85-23.54 32.84-142.92 41.39-180ZM43.46-433.54l15-60h199.62l-15 60H43.46Zm80-142.92 15-60h239.62l-15 60H123.46ZM290-240q20.85 0 35.42-14.58Q340-269.15 340-290t-14.58-35.42Q310.85-340 290-340t-35.42 14.58Q240-310.85 240-290t14.58 35.42Q269.15-240 290-240Zm395.39 0q20.84 0 35.42-14.58 14.58-14.57 14.58-35.42t-14.58-35.42Q706.23-340 685.39-340q-20.85 0-35.43 14.58-14.58 14.57-14.58 35.42t14.58 35.42Q664.54-240 685.39-240Z" />
  </svg>
);

/** Material Symbol `where_to_vote`. */
export const WhereToVoteIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="m438-441 184.15-184.15-42.77-42.77L438-526.54l-56-56-42.77 42.77L438-441Zm42 261.54q117.38-105.08 179.65-201.58 62.27-96.5 62.27-169.04 0-109.38-69.5-179.84-69.5-70.46-172.42-70.46-102.92 0-172.42 70.46-69.5 70.46-69.5 179.84 0 72.54 62.27 169.04 62.27 96.5 179.65 201.58Zm0 79.84Q329-230.46 253.54-343.15q-75.46-112.7-75.46-206.93 0-138.46 89.57-224.19Q357.23-860 480-860t212.35 85.73q89.57 85.73 89.57 224.19 0 94.23-75.46 206.93Q631-230.46 480-99.62Zm0-458.07Z" />
  </svg>
);

/** Material Symbol `dashboard`. */
export const DashboardIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M530-600v-220h290v220H530ZM140-460v-360h290v360H140Zm390 320v-360h290v360H530Zm-390 0v-220h290v220H140Zm60-380h170v-240H200v240Zm390 320h170v-240H590v240Zm0-460h170v-100H590v100ZM200-200h170v-100H200v100Zm170-320Zm220-140Zm0 220ZM370-300Z" />
  </svg>
);

/** Material Symbol `favorite`. */
export const FavoriteIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="m480-146.93-44.15-39.69q-99.46-90.23-164.5-155.07-65.04-64.85-103.08-115.43-38.04-50.57-53.15-92.27Q100-591.08 100-634q0-85.15 57.42-142.58Q214.85-834 300-834q52.38 0 99 24.5t81 70.27q34.38-45.77 81-70.27 46.62-24.5 99-24.5 85.15 0 142.58 57.42Q860-719.15 860-634q0 42.92-15.12 84.61-15.11 41.7-53.15 92.27-38.04 50.58-102.89 115.43Q624-276.85 524.15-186.62L480-146.93Zm0-81.07q96-86.38 158-148.08 62-61.69 98-107.19t50-80.81q14-35.3 14-69.92 0-60-40-100t-100-40q-47.38 0-87.58 26.88-40.19 26.89-63.65 74.81h-57.54q-23.85-48.31-63.85-75Q347.38-774 300-774q-59.62 0-99.81 40Q160-694 160-634q0 34.62 14 69.92 14 35.31 50 80.81t98 107q62 61.5 158 148.27Zm0-273Z" />
  </svg>
);

/** Material Symbol `credit_card`. */
export const CreditCardIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M860-707.69v455.38Q860-222 839-201q-21 21-51.31 21H172.31Q142-180 121-201q-21-21-21-51.31v-455.38Q100-738 121-759q21-21 51.31-21h615.38Q818-780 839-759q21 21 21 51.31Zm-700 83.85h640v-83.85q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H172.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v83.85Zm0 127.68v243.85q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h615.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-243.85H160ZM160-240v-480 480Z" />
  </svg>
);

/** Material Symbol `sell`. */
export const SellIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M838.31-391.92 568.08-121.69q-10.9 10.84-24.53 16.27-13.63 5.42-27.18 5.42-13.56 0-27.12-5.42-13.56-5.43-24.4-16.27L121.08-465.08q-10.23-9.84-15.66-23.1-5.42-13.26-5.42-27.9v-271.23q0-29.98 21.19-51.34 21.2-21.35 51.5-21.35h271.23q14.49 0 28.07 5.88 13.59 5.87 23.55 15.81l342.77 343.39q10.99 10.9 16.03 24.52 5.04 13.63 5.04 27.33 0 13.7-5.04 26.94-5.04 13.25-16.03 24.21Zm-313 228.07 270.23-270.23q3.84-3.84 3.84-9.04 0-5.19-3.84-9.03L448.31-799.62H172.69q-5.38 0-9.04 3.47-3.65 3.46-3.65 8.84v270.62q0 2.31.77 4.61.77 2.31 2.69 4.23l343.77 344q3.85 3.85 9.04 3.85 5.19 0 9.04-3.85ZM261-648.85q20.92 0 35.54-14.58 14.61-14.58 14.61-35.42 0-20.99-14.58-35.68-14.58-14.7-35.42-14.7-20.99 0-35.68 14.65-14.7 14.65-14.7 35.58 0 20.92 14.65 35.54 14.65 14.61 35.58 14.61Zm219.77 169.62Z" />
  </svg>
);

/** Material Symbol `info`. */
export const InfoIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M450-290h60v-230h-60v230Zm30-298.46q13.73 0 23.02-9.29t9.29-23.02q0-13.73-9.29-23.02-9.29-9.28-23.02-9.28t-23.02 9.28q-9.29 9.29-9.29 23.02t9.29 23.02q9.29 9.29 23.02 9.29Zm.07 488.46q-78.84 0-148.21-29.92t-120.68-81.21q-51.31-51.29-81.25-120.63Q100-401.1 100-479.93q0-78.84 29.92-148.21t81.21-120.68q51.29-51.31 120.63-81.25Q401.1-860 479.93-860q78.84 0 148.21 29.92t120.68 81.21q51.31 51.29 81.25 120.63Q860-558.9 860-480.07q0 78.84-29.92 148.21t-81.21 120.68q-51.29 51.31-120.63 81.25Q558.9-100 480.07-100Zm-.07-60q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
  </svg>
);

/** Material Symbol `chat`. */
export const ChatIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M250-410h300v-60H250v60Zm0-120h460v-60H250v60Zm0-120h460v-60H250v60ZM100-118.46v-669.23Q100-818 121-839q21-21 51.31-21h615.38Q818-860 839-839q21 21 21 51.31v455.38Q860-302 839-281q-21 21-51.31 21H241.54L100-118.46ZM216-320h571.69q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-455.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H172.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v523.08L216-320Zm-56 0v-480 480Z" />
  </svg>
);

/** Material Symbol `description`. */
export const DescriptionIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M330-250h300v-60H330v60Zm0-160h300v-60H330v60Zm-77.69 310Q222-100 201-121q-21-21-21-51.31v-615.38Q180-818 201-839q21-21 51.31-21H570l210 210v477.69Q780-142 759-121q-21 21-51.31 21H252.31ZM540-620v-180H252.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v615.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h455.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46V-620H540ZM240-800v180-180V-160v-640Z" />
  </svg>
);

/** Material Symbol `logout`. */
export const LogoutIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M212.31-140Q182-140 161-161q-21-21-21-51.31v-535.38Q140-778 161-799q21-21 51.31-21h268.07v60H212.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v535.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h268.07v60H212.31Zm436.92-169.23-41.54-43.39L705.08-450H363.85v-60h341.23l-97.39-97.38 41.54-43.39L820-480 649.23-309.23Z" />
  </svg>
);

/** Material Symbol `tune`. */
export const TuneIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M450-130v-220h60v80h320v60H510v80h-60Zm-320-80v-60h220v60H130Zm160-160v-80H130v-60h160v-80h60v220h-60Zm160-80v-60h380v60H450Zm160-160v-220h60v80h160v60H670v80h-60Zm-480-80v-60h380v60H130Z" />
  </svg>
);

/** Material Symbol `support_agent`. */
export const SupportAgentIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="M455.39-140v-60h292.3q5 0 8.66-3.08 3.65-3.07 3.65-8.07v-279.77q0-114.7-82.08-193.12-82.07-78.42-197.92-78.42t-197.92 78.42Q200-605.62 200-490.92v235.53h-30q-28.77 0-49.38-20.03Q100-295.46 100-324.23v-77.69q0-19.46 11.08-35.66 11.08-16.19 28.92-26.03l1.85-51.08q4.92-65.31 33.92-121t74.38-96.96q45.39-41.27 104.77-64.31Q414.31-820 480-820t124.77 23.04q59.08 23.04 104.77 64t74.38 96.65q28.69 55.7 34.23 121l1.85 50.08q17.46 8.23 28.73 23.54Q860-426.38 860-407.54v89.31q0 18.84-11.27 34.15-11.27 15.31-28.73 23.54v49.39q0 29.53-21.19 50.34Q777.61-140 747.69-140h-292.3Zm-87.7-269.23q-14.69 0-25.04-9.96-10.34-9.96-10.34-24.66 0-14.69 10.34-24.84 10.35-10.16 25.04-10.16 14.7 0 25.04 10.16 10.35 10.15 10.35 24.84 0 14.7-10.35 24.66-10.34 9.96-25.04 9.96Zm224.62 0q-14.7 0-25.04-9.96-10.35-9.96-10.35-24.66 0-14.69 10.35-24.84 10.34-10.16 25.04-10.16 14.69 0 25.04 10.16 10.34 10.15 10.34 24.84 0 14.7-10.34 24.66-10.35 9.96-25.04 9.96ZM254.85-472q-6.23-97.92 60.92-167.58 67.15-69.65 166.23-69.65 83.23 0 146.88 51.5 63.66 51.5 77.27 133.34-85.23-1-157.5-44.76-72.27-43.77-110.96-120-15.23 74.61-63.84 131.92-48.62 57.31-119 85.23Z" />
  </svg>
);

/** Material Symbol `map`. */
export const MapIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 -960 960 960"
    fill="currentColor"
    aria-hidden
    className={className}
  >
    <path d="m600-141.54-240-84-172.15 66.61q-17.69 6.85-32.77-3.92Q140-173.62 140-192.31v-524.61q0-11.85 6.35-21.27 6.34-9.42 17.81-13.65L360-818.46l240 84 172.15-66.61q17.69-6.85 32.77 3.34Q820-787.54 820-769.23v527.69q0 12.23-6.92 21.46-6.93 9.23-18.77 13.46L600-141.54Zm-30-73.38v-468l-180-62.93v468l180 62.93Zm60 0L760-258v-474l-130 49.08v468ZM200-228l130-49.85v-468L200-702v474Zm430-454.92v468-468Zm-300-62.93v468-468Z" />
  </svg>
);
